import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import { useCreateNewChat } from '@/chat-common/new_chat/hooks/useCreateNewChat'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { Header } from '@/components/header/Header'
import { SearchField } from '@/components/search-field/SearchField'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'
import { usePollWhileVisible } from '@/hooks/usePollWhileVisible'
import { kDraftsPollIntervalMs } from '@/drafting/constants'
import { DraftsTable } from '@/drafting/destination/components/DraftsTable'
import { useExportDraft } from '@/drafting/destination/hooks/useExportDraft'
import { useDraftsListView } from '@/drafting/destination/hooks/useDraftsListView'
import { useMatterArtifactDrafts } from '@/drafting/hooks/useMatterArtifactDrafts'
import { useNavigateToDraftInChat } from '@/drafting/hooks/useNavigateToDraftInChat'
import { matterPaths } from '@/routing/paths'
import { selectActiveMatter } from '@/matter/store/selectors'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'

/**
 * DraftsPage
 *
 * The matter-scoped Drafts destination at /dashboard/m/:matterId/drafts, behind DRAFTS_DESTINATION.
 */
export default function DraftsPage() {
  const navigate = useNavigate()
  const matter = useAppSelector((state: RootState) => selectActiveMatter(state))
  const { matterId, rows, isError, isLoadingWithoutCachedDrafts, refetch } = useMatterArtifactDrafts()
  const navigateToDraftInChat = useNavigateToDraftInChat()
  const { createNewChat } = useCreateNewChat()
  const { searchQuery, setSearchQuery, clearSearch, sorting, onSortingChange } = useDraftsListView(matterId)
  const { downloadDraft, saveDraftToMatterFiles, exportingMatterArtifactIds } = useExportDraft(matterId)
  const isAccountCollaborationCapable = useIsAccountCollaborationCapable()

  // Entering the page refetches, but a page left open hears nothing about a change made anywhere else:
  // only the tab that makes one marks the drafts stale. Polling while visible lets a deleted or edited
  // draft correct itself. A personal account does not poll, because only another member's change is
  // expected to land while the page sits open; that user's own second tab is caught on the next entry.
  usePollWhileVisible(refetch, kDraftsPollIntervalMs, isAccountCollaborationCapable)

  // Drafts is the active crumb, so it carries no handler and renders as plain text.
  const breadcrumbItems: BreadcrumbItemType[] = matter
    ? [
        { id: matter.id, label: matter.name, onClick: () => navigate(matterPaths.overview(matter.id)) },
        { id: 'drafts', label: 'Drafts' },
      ]
    : []

  const searchFieldRef = useRef<HTMLInputElement>(null)
  const handleClearSearch = () => {
    clearSearch()
    searchFieldRef.current?.focus()
  }

  const handleEmptyAction = () => {
    if (matterId) createNewChat(true, matterId)
  }

  // Held for a short minimum so a sub-100ms fetch doesn't flash six rows.
  const withinMinimumDisplay = useDelayedVisibility(isLoadingWithoutCachedDrafts, { showDelayMs: 0, minDisplayMs: 500 })
  const showLoadingSkeletons = isLoadingWithoutCachedDrafts || withinMinimumDisplay

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <Header breadcrumbs={breadcrumbItems} />
      {/* Full width by Design's choice, and no bottom padding so a long list runs off the page. */}
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-0 pt-6 lg:px-10">
        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4 lg:gap-6">
          <div className="flex shrink-0 flex-col gap-4">
            {/* Below desktop the header's breadcrumb already names the page, so the heading is kept for screen readers only. */}
            <h1 className="font-rethink-sans text-xl font-semibold not-italic leading-7 text-brand-neutral-950 max-lg:sr-only">Drafts</h1>
            <SearchField ref={searchFieldRef} value={searchQuery} onChange={setSearchQuery} placeholder="Search drafts" aria-label="Search drafts" />
          </div>
          <DraftsTable
            rows={rows}
            searchQuery={searchQuery}
            onClearSearch={handleClearSearch}
            sorting={sorting}
            onSortingChange={onSortingChange}
            isLoadingWithoutCachedDrafts={showLoadingSkeletons}
            isError={isError}
            onRetry={refetch}
            onOpenDraft={(row) =>
              navigateToDraftInChat({
                artifactVersionId: row.artifactVersionId,
                conversationId: row.conversationId,
                draftEntryPoint: DraftEntryPoint.DraftsPage,
              })
            }
            onDownloadDraft={downloadDraft}
            onSaveDraftToMatterFiles={saveDraftToMatterFiles}
            exportingMatterArtifactIds={exportingMatterArtifactIds}
            onEmptyAction={handleEmptyAction}
          />
        </div>
      </div>
    </div>
  )
}
